import { useMemo, useState } from 'react';
import * as Icons from '@components/Icons';
import type { CenterDaySchedule } from '@api/types';
import {
  TIME_RANGES,
  DEFAULT_PALETTE,
  deriveRanges,
  type TimeRange,
  type CentreMeta,
  type StaffRow,
  type DayCell,
} from './horarios-shared';

/** Centre opening hours indexed 0=Sun…6=Sat. null = no businessHours (24/7). */
export type CentreBusinessHours = ReadonlyMap<string, readonly (CenterDaySchedule | null)[] | null>;

interface AssignShiftModalProps {
  staff: StaffRow;
  dayLabel: string;
  /** 0=Mon … 6=Sun (grid convention). */
  dayOfWeek: number;
  existing: DayCell | null;
  centreMeta: ReadonlyMap<string, CentreMeta>;
  centresBh: CentreBusinessHours;
  isSaving: boolean;
  onClose: () => void;
  onSave: (centerId: string, timeRange: string) => void;
  onRemove: () => void;
}

/** "Asignar turno" — pick a centre (among the staff's centres) + a time range
 *  derived from that centre's opening hours for the day. Includes a free
 *  "A medida" picker bounded to the centre's businessHours. */
export function AssignShiftModal({ staff, dayLabel, dayOfWeek, existing, centreMeta, centresBh, isSaving, onClose, onSave, onRemove }: AssignShiftModalProps) {
  const [centre, setCentre] = useState<string>(existing?.centerId ?? (staff.centres.length === 1 ? staff.centres[0]?.id ?? '' : ''));
  const [range, setRange] = useState<string>(existing?.timeRange ?? '');
  const [mode, setMode] = useState<'preset' | 'custom'>(() => deriveInitialMode(existing, dayOfWeek, centresBh));
  const [customStart, setCustomStart] = useState<string>(() => existing?.timeRange.split('-')[0] ?? '');
  const [customEnd, setCustomEnd] = useState<string>(() => existing?.timeRange.split('-')[1] ?? '');

  // Franjas para el centro elegido: 'closed' (cerrado ese día), null (sin centro
  // elegido), o la lista de presets. Centro sin businessHours (null) → fijas.
  const dayRanges = useMemo<TimeRange[] | 'closed' | null>(() => {
    if (!centre) return null;
    const bh = centresBh.get(centre);
    if (bh === null || bh === undefined) return [...TIME_RANGES]; // 24/7 → presets fijos
    const day = bh[(dayOfWeek + 1) % 7] ?? null; // grid 0=Lun → businessHours 0=Dom
    if (!day) return 'closed';
    return deriveRanges(day.open, day.close);
  }, [centre, centresBh, dayOfWeek]);

  // Bounds para el picker custom. null cuando es 24/7 (sin restricción) o cuando
  // no hay centro elegido.
  const customBounds = useMemo<{ open: string; close: string } | null>(() => {
    if (!centre) return null;
    const bh = centresBh.get(centre);
    if (bh === null || bh === undefined) return null;
    const day = bh[(dayOfWeek + 1) % 7] ?? null;
    return day ? { open: day.open, close: day.close } : null;
  }, [centre, centresBh, dayOfWeek]);

  const isClosed = dayRanges === 'closed';
  const ranges = Array.isArray(dayRanges) ? dayRanges : [];

  const customRange = customStart && customEnd ? `${customStart}-${customEnd}` : '';
  const customValid = isCustomValid(customStart, customEnd, customBounds, dayRanges !== null);

  const canSave =
    centre !== '' &&
    !isClosed &&
    (mode === 'preset'
      ? ranges.some((r) => r.value === range)
      : customValid);

  return (
    <div
      className="modal-scrim"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
    >
      <div className="modal" style={{ width: 'min(460px, 100%)' }}>
        <div className="modal-head">
          <div>
            <h2 className="modal-title">Asignar turno</h2>
            <p className="page-sub" style={{ margin: '4px 0 0' }}>{staff.name} · {dayLabel}</p>
          </div>
          <button className="icon-btn" data-variant="ghost" onClick={onClose} aria-label="Cerrar">
            <Icons.X size={14} />
          </button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="field">
            <label className="field-label">Centro</label>
            <div style={{ display: 'grid', gridTemplateColumns: staff.centres.length > 1 ? '1fr 1fr' : '1fr', gap: 8 }}>
              {staff.centres.map((c) => {
                const meta = centreMeta.get(c.id);
                const sel = c.id === centre;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => { setCentre(c.id); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 12px',
                      borderRadius: 'var(--radius-sm)',
                      border: sel ? `1px solid ${meta?.palette.dot ?? 'var(--accent)'}` : '1px solid var(--border)',
                      background: sel ? meta?.palette.tint : 'transparent',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: meta?.palette.dot ?? DEFAULT_PALETTE.dot, flexShrink: 0 }} />
                    <span style={{ fontSize: 13 }}>{c.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="field">
            <label className="field-label">Franja horaria</label>
            {!centre ? (
              <p style={{ fontSize: 12.5, color: 'var(--fg-soft)', margin: 0 }}>Elige un centro primero.</p>
            ) : isClosed ? (
              <div style={{ padding: '10px 12px', background: 'rgba(169, 18, 17, 0.06)', border: '1px dashed rgba(169, 18, 17, 0.35)', borderRadius: 'var(--radius-sm)', fontSize: 12.5, color: '#A91211' }}>
                Este centro está cerrado ese día. Cambia el horario de apertura en Configuración para poder asignar turno.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {ranges.map((r) => {
                  const selected = mode === 'preset' && r.value === range;
                  return (
                    <button
                      key={r.value}
                      type="button"
                      className="row-menu-item"
                      style={{
                        fontSize: 13,
                        background: selected ? 'var(--accent-soft)' : undefined,
                        color: selected ? 'var(--accent-dark)' : undefined,
                      }}
                      onClick={() => { setMode('preset'); setRange(r.value); }}
                    >
                      <span className="mono" style={{ minWidth: 95 }}>{r.value}</span>
                      <span>{r.label}</span>
                      <span className="mono" style={{ marginLeft: 'auto', color: 'var(--fg-soft)' }}>{r.hours}h</span>
                    </button>
                  );
                })}
                <button
                  type="button"
                  className="row-menu-item"
                  style={{
                    fontSize: 13,
                    background: mode === 'custom' ? 'var(--accent-soft)' : undefined,
                    color: mode === 'custom' ? 'var(--accent-dark)' : undefined,
                  }}
                  onClick={() => { setMode('custom'); setRange(''); }}
                >
                  <span className="mono" style={{ minWidth: 95 }}>—</span>
                  <span>A medida</span>
                  <span style={{ marginLeft: 'auto', color: 'var(--fg-soft)', fontSize: 12 }}>Elige inicio y fin</span>
                </button>
                {mode === 'custom' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'var(--bg-soft)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span className="field-label" style={{ fontSize: 11 }}>Inicio</span>
                        <input
                          type="time"
                          className="input mono"
                          value={customStart}
                          {...(customBounds ? { min: customBounds.open, max: customBounds.close } : {})}
                          onChange={(e) => { setCustomStart(e.target.value); }}
                        />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span className="field-label" style={{ fontSize: 11 }}>Fin</span>
                        <input
                          type="time"
                          className="input mono"
                          value={customEnd}
                          {...(customBounds ? { min: customBounds.open, max: customBounds.close } : {})}
                          onChange={(e) => { setCustomEnd(e.target.value); }}
                        />
                      </label>
                    </div>
                    {customStart && customEnd && !customValid && (
                      <p style={{ margin: 0, fontSize: 12, color: '#A91211' }}>
                        {customBounds
                          ? `Debe estar dentro de ${customBounds.open}–${customBounds.close} y el fin ser posterior al inicio.`
                          : 'El fin debe ser posterior al inicio.'}
                      </p>
                    )}
                    {customValid && (
                      <p style={{ margin: 0, fontSize: 12, color: 'var(--fg-soft)' }}>
                        <span className="mono">{customRange}</span> · <span className="mono">{customHoursLabel(customStart, customEnd)}h</span>
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="modal-foot" style={{ justifyContent: 'space-between' }}>
          {existing ? (
            <button className="btn" data-variant="danger" onClick={onRemove} disabled={isSaving}>
              <Icons.Trash size={12} /> Quitar turno
            </button>
          ) : <span />}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" onClick={onClose} disabled={isSaving}>Cancelar</button>
            <button
              className="btn"
              data-variant="primary"
              disabled={!canSave || isSaving}
              onClick={() => { onSave(centre, mode === 'custom' ? customRange : range); }}
            >
              {isSaving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function deriveInitialMode(
  existing: DayCell | null,
  dayOfWeek: number,
  centresBh: CentreBusinessHours,
): 'preset' | 'custom' {
  if (!existing) return 'preset';
  const bh = centresBh.get(existing.centerId);
  const presets: TimeRange[] = bh === null || bh === undefined
    ? [...TIME_RANGES]
    : (() => {
        const day = bh[(dayOfWeek + 1) % 7] ?? null;
        return day ? deriveRanges(day.open, day.close) : [];
      })();
  return presets.some((p) => p.value === existing.timeRange) ? 'preset' : 'custom';
}

function isCustomValid(
  start: string,
  end: string,
  bounds: { open: string; close: string } | null,
  hasCentre: boolean,
): boolean {
  if (!hasCentre) return false;
  if (!start || !end) return false;
  if (start >= end) return false;
  if (bounds && (start < bounds.open || end > bounds.close)) return false;
  return true;
}

function customHoursLabel(start: string, end: string): string {
  const sMin = Number(start.slice(0, 2)) * 60 + Number(start.slice(3, 5));
  const eMin = Number(end.slice(0, 2)) * 60 + Number(end.slice(3, 5));
  const hours = (eMin - sMin) / 60;
  return Number.isInteger(hours) ? String(hours) : hours.toFixed(1);
}
